import type {
  TRIReportDataType,
  TRIWasteTreatmentDetailsDataType,
  TRIWasteTreatmentMethodDataType,
} from '../../types/form-r.ts';
import type { QuestionnaireAnswers } from '../../types/questionnaires.ts';
import type { CasOrCategory } from '../../types/db.ts';
import type { Warning } from '../../types/tri.ts';

export function formRSection7aWasteTreatment({
  chemicalName,
  casOrCategory,
  wasteTreatment,
  warnings,
}: {
  chemicalName: string;
  casOrCategory: CasOrCategory;
  wasteTreatment: QuestionnaireAnswers['wasteTreatment'];
  warnings: Warning[];
}): TRIReportDataType['WasteTreatment'] {
  if (wasteTreatment.isApplicable) {
    return {
      choice: 'WasteTreatmentDetails',
      value: Object.values(wasteTreatment.treatments).flatMap(
        (treatment, treatmentIndex): TRIWasteTreatmentDetailsDataType[] => {
          if (!treatment || (Array.isArray(treatment.chemicals) && !treatment.chemicals.includes(casOrCategory))) {
            return [];
          }

          if (treatment.wasteStreamCode === '') {
            warnings.push({
              warningID: 'TW1016',
              type: 'questionnaire',
              subType: 'wasteTreatment',
              casOrCategory,
              message: `${chemicalName} (${casOrCategory}), waste treatment #${treatmentIndex + 1} did not specify a waste stream code.`,
              blocksPriorYearSection8Calculation: false,
            });
          }

          if (treatment.treatmentEfficiency === '') {
            warnings.push({
              warningID: 'TW1017',
              type: 'questionnaire',
              subType: 'wasteTreatment',
              casOrCategory,
              message: `${chemicalName} (${casOrCategory}), waste treatment #${treatmentIndex + 1} did not specify a treatment efficiency.`,
              blocksPriorYearSection8Calculation: false,
            });
          }

          if (treatment.treatmentMethodSequence.length === 0) {
            warnings.push({
              warningID: 'TW1123',
              type: 'questionnaire',
              subType: 'wasteTreatment',
              casOrCategory,
              message: `${chemicalName} (${casOrCategory}), waste treatment #${treatmentIndex + 1} did not specify any treatment method sequence codes.`,
              blocksPriorYearSection8Calculation: false,
            });
          }

          return [
            {
              WasteStreamSequenceNumber: treatmentIndex + 1,
              WasteStreamTypeCode: treatment.wasteStreamCode,
              WasteTreatmentMethod: treatment.treatmentMethodSequence.map(
                (method, methodIndex): TRIWasteTreatmentMethodDataType => {
                  return {
                    WasteTreatmentSequenceNumber: methodIndex + 1,
                    WasteTreatmentMethodCode: method,
                  };
                },
              ),
              TreatmentEfficiency: {
                choice: 'TreatmentEfficiencyRangeCode',
                value: treatment.treatmentEfficiency === '' ? null : treatment.treatmentEfficiency,
              },
              InfluentConcentrationRangeCode: null, // "Unused starting RY2005."
              OperatingDataIndicator: null, // "Unused starting RY2005."
            },
          ];
        },
      ),
    };
  } else {
    return {
      choice: 'NA',
      value: true,
    };
  }
}
